import React, { useEffect, useState } from "react";
import {
  Grid,
  Divider,
  Typography,
  FormControl,
  MenuItem,
  TextField,
  InputAdornment,
  List,
  ListItem,
  Checkbox,
  Select,
  ListItemText,
  IconButton,
  FormHelperText,
  Collapse,
} from "@material-ui/core";
import _ from "lodash";
import { useStyles } from "./styles";
import clsx from "clsx";
import { Controller } from "react-hook-form";
import { DateTimePicker } from "@material-ui/pickers";
import ClearIcon from "@material-ui/icons/Clear";
import IconExpandLess from "@material-ui/icons/ExpandLess";
import IconExpandMore from "@material-ui/icons/ExpandMore";

const parseValue = (value) => {
  if (!value) return [];
  return Array.isArray(value)
    ? value
    : value.split(",").map(s => s.trim()).filter(Boolean);
};

const getDefaultValue = (item, isEditing, information) => {
  if (!item) return "";
  if (isEditing) {
    return item.allowmultiplevalues ? parseValue(item.currentvalue) : item.currentvalue ?? "";
  }

  if (item.parametername === "selectedFacilities" && information?.facilityID) {
    return [information.facilityID];
  }

  if (item.allowmultiplevalues) {
    return parseValue(item.currentvalue) || parseValue(item.defaultvalue) || [];
  }

  return item.currentvalue ?? item.defaultvalue ?? "";
};

const groupParameters = (items) => {
  return items.filter(item => item.visible).reduce((groups, item) => {
    const groupKey = item.description.includes(".")
      ? item.description.split(".")[0]
      : "";
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(item);
    return groups;
  }, {});
};

const parseOptions = (item) => {
  if (!item.usedropdown || !item.payload) return [];
  try {
    const parsed = JSON.parse(item.payload);
    let options;

    if (Array.isArray(parsed)) {
      options = parsed.map(entry => ({
        label: entry[item.fieldnamedisplay] || entry.Description || "",
        value: String(entry[item.fieldnamevalue] || entry.Value || "")
      }));
    } else {
      options = Object.entries(parsed).map(([key, value]) => ({
        label: key,
        value: value.toString()
      }));
    }

    options = _.uniqBy(options, opt => {
      const normalizedValue = opt.value == null || opt.value === "" ? null : opt.value;
      const normalizedLabel = opt.label == null || opt.label === "" ? null : opt.label;
      return `${normalizedValue}|||${normalizedLabel}`;
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  } catch (e) {
    console.error("Invalid payload JSON", e);
    return [];
  }
};

const renderSelectValue = (selected, options, allowMultiple) => {
  if (allowMultiple) {
    const selectedArray = Array.isArray(selected)
      ? selected
      : selected
        ? [selected]
        : [];
    if (selectedArray.length === 0) return "";
    if (selectedArray.length === options.length && options.length > 1)
      return "All selected";
    const selectedLabels = selectedArray.map((val) => {
      const opt = options.find((o) => String(o.value) === String(val));
      return opt ? opt.label : String(val);
    });
    return selectedLabels.join(", ");
  }
  const opt = options.find((o) => String(o.value) === String(selected));
  return opt ? opt.label : String(selected || "");
};

const ReportParametersList = ({
  items,
  control,
  setValue,
  getValues,
  errors,
  information,
  isEditing,
  isReportTypeChanged
}) => {
  const classes = useStyles();
  const groupedItems = groupParameters(items);
  const setSupportingParameters = (items) => {

    // Set all the invisible parameters.
    const invisibleParamsMap = {
      facilityid: information.facilityID,
      facilitygroupid: information.facilityGroupID,
      userid: information.userID,
      validfacility: information.facilityNames,
      facilitygroupname: information.facilityGroupName,
      paramuserid: "",
      resolution: "",
    };

    items.filter(item => !item.visible).forEach(item => {
      const value = invisibleParamsMap[item.parametername];
      if (value !== undefined && value !== "") {
        setValue(item.parametername, value ?? "");
      }
    });

    // Set all supporting parameters that might be visible or not depending upon the report.
    const selectedFacParam = items.find(item => item.parametername === "selectedFacilities");
    if (selectedFacParam) {
      let facilityValue;
      if(selectedFacParam.reportid === 17 && information.facilityGroupID && !selectedFacParam.visible) {
        facilityValue = "";
      } else {
        facilityValue = information.facilityGroupID && !selectedFacParam.visible
          ? information.facilityGroupID
          : [information.facilityID];
      }

      if (!isEditing || isReportTypeChanged) {
        setValue(selectedFacParam.parametername, facilityValue);
      }
    }
  };

  const [openGroups, setOpenGroups] = useState({});
  const [filteredOptionsMap, setFilteredOptionsMap] = useState({});

  const toggleGroup = (groupKey) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  useEffect(() => {
    items.forEach(item => {
      let defaultValue;
      if (
        !isEditing &&
        item.allowmultiplevalues &&
        item.selectall &&
        (!item.currentvalue || item.currentvalue.trim() === "") &&
        (!item.defaultvalue || item.defaultvalue.trim() === "")
      ) {
        const options = parseOptions(item);
        defaultValue = options.map(opt => opt.value);
      } else {
        defaultValue = getDefaultValue(item, isEditing, information);
      }
      setValue(item.parametername, defaultValue);
    });
    setSupportingParameters(items);

    const initialOpenGroups = Object.keys(groupParameters(items)).reduce((acc, group) => {
      if (group !== "") {
        acc[group] = true;
      }
      return acc;
    }, {});
    setOpenGroups(initialOpenGroups);
  }, [items, setValue, isEditing]);

  useEffect(() => {
    const initialFilteredOptions = items
      .filter(item => item.usedropdown)
      .reduce((acc, item) => {
        acc[item.parametername] = filterOptions(item, parseOptions(item));
        return acc;
      }, {});

    setFilteredOptionsMap(initialFilteredOptions);
  }, [items]);

  const handleSelectChange = (item, value, options) => {
    if (item.allowmultiplevalues) {
      if (Array.isArray(value) && value.includes("SelectAll")) {
        const allOptions = options.map((opt) => opt.value);
        const currentValues = getValues(item.parametername) || [];
        const isAllSelected = currentValues.length === allOptions.length;
        setValue(item.parametername, isAllSelected ? [] : allOptions);
      } else {
        setValue(item.parametername, value);
      }
    } else {
      setValue(item.parametername, value);
    }
    updateFilteredOptionsAndSelections();
  };

  const updateFilteredOptionsAndSelections = () => {
    const updatedFilteredOptions = items.reduce((acc, currentItem) => {
      if (currentItem.usedropdown) {
        acc[currentItem.parametername] = filterOptions(currentItem, parseOptions(currentItem));
      }
      return acc;
    }, {});

    setFilteredOptionsMap(updatedFilteredOptions);

    items.filter((currentItem) => currentItem.usedropdown).forEach((currentItem) => {
      const currentValue = getValues(currentItem.parametername);
      const validOptions = updatedFilteredOptions[currentItem.parametername]?.map(opt => opt.value) || [];

      if (Array.isArray(currentValue)) {
        setValue(currentItem.parametername, currentValue.filter(val => validOptions.includes(val)));
      } else if (!validOptions.includes(currentValue)) {
        setValue(currentItem.parametername, "");
      }
    });
  };

  const filterOptions = (parameter, options) => {
    if (!parameter.filterString) { // No filtering needed
      return options;
    }
    // Extract fieldName to compare with and the parameter references from the filterString
    const match = parameter.filterString.match(/\[(.*?)\] In \((.*?)\)/);
    if (!match) {
      const result = JSON.parse(parameter.payload).map(item => ({
        label: item[parameter.fieldnamedisplay],
        value: String(item[parameter.fieldnamevalue])
      }));
      return result;
    }

    const fieldName = match[1];
    const referencedParams = match[2]
      .replace(/\?/g, "")
      .split(",")
      .map((param) => param.trim());

    // Get all the values that are selected on the referenced parameters.
    const selectedValues = referencedParams.flatMap((paramName) => {
      return getValues(paramName) || [];
    });

    // Extract static values passed in FilterString denoted by curly braces
    const additionalStaticValues = referencedParams
      .filter(paramName => paramName.startsWith("{") && paramName.endsWith("}"))
      .map(paramName => paramName.slice(1, -1)); // Remove `{}`
    selectedValues.push(...additionalStaticValues);

    // Filter the options based on fieldName and selectedValues
    const filteredValue = JSON.parse(parameter.payload)
      .filter((item) => selectedValues.includes(item[fieldName]))
      .map(item => ({
        label: item[parameter.fieldnamedisplay],
        value: String(item[parameter.fieldnamevalue])
      }));

    // Get items with unique value only
    const uniqueValuesSet = new Set();
    const uniqueFilteredValue = filteredValue.filter(item => {
      if (uniqueValuesSet.has(item.value)) {
        return false;
      }
      uniqueValuesSet.add(item.value);
      return true;
    });

    return parameter.description === "Device Name" ? uniqueFilteredValue : uniqueFilteredValue.sort((a, b) => a.label.localeCompare(b.label));
  };

  const renderInput = (item, errorMessage) => {

    const options = filteredOptionsMap[item.parametername] || [];
    const isDateTime = item.fieldtype && item.fieldtype.toLowerCase() === 'datetime';
    const isBoolean = item.fieldtype && item.fieldtype.toLowerCase() === 'bool';

    if (item.usedropdown) {
      return (
        <Controller
          name={item.parametername}
          control={control}
          defaultValue={[]}
          render={({ field }) => (
            <FormControl
              variant="outlined"
              className={classes.select}
              size="small"
            >
              <Select
                {...field}
                labelId={`${item.parametername}-label`}
                multiple={item.allowmultiplevalues}
                value={
                  item.allowmultiplevalues
                    ? Array.isArray(field.value)
                      ? field.value
                      : []
                    : field.value
                }
                error={!!errorMessage}
                displayEmpty
                endAdornment={
                  item.allownull &&
                  ((item.allowmultiplevalues &&
                    Array.isArray(field.value) &&
                    field.value.length > 0) ||
                    (!item.allowmultiplevalues && field.value !== "")) && (
                    <InputAdornment position="start">
                      <IconButton
                        size="small"
                        onClick={() =>
                          field.onChange(
                            item.allowmultiplevalues ? [] : ""
                          )
                        }
                        edge="start"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }
                renderValue={(selected) =>
                  renderSelectValue(selected, options, item.allowmultiplevalues)
                }
                onChange={(e) => {
                  handleSelectChange(item, e.target.value, options);
                }}
              >
                {item.allowmultiplevalues &&
                  options.length > 0 && (
                    <MenuItem divider={Divider} value="SelectAll">
                      <Checkbox
                        checked={
                          Array.isArray(field.value) &&
                          field.value.length === options.length
                        }
                      />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                  )}
                {options.length > 0 ? (
                  options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {item.allowmultiplevalues && (
                        <Checkbox
                          checked={
                            Array.isArray(field.value) &&
                            field.value.some((val) => String(val) === String(opt.value))
                          }
                        />
                      )}
                      <ListItemText primary={opt.label} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No data to display</MenuItem>
                )}
              </Select>
              {errorMessage &&
                <FormHelperText
                  error={!!errorMessage}
                >
                  {errorMessage}
                </FormHelperText>}
            </FormControl>
          )}
        />
      );
    } else if (isDateTime) {
      return (
        <Controller
          name={item.parametername}
          control={control}
          defaultValue=""
          render={({ field: { ref, ...fieldProps } }) => (
            <DateTimePicker
              {...fieldProps}
              size="small"
              value={fieldProps.value || null}
              error={!!errorMessage}
              helperText={errorMessage}
              onChange={(date) => fieldProps.onChange(date)}
              className={classes.dateTimePicker}
              format="MM/DD/YY hh:mm a"
              inputVariant="outlined"
            />
          )}
        />
      );
    } else if (isBoolean) {
      return (
        <Controller
          name={item.parametername}
          control={control}
          defaultValue={null}
          render={({ field }) => (
            <FormControl variant="outlined" className={classes.select} size="small">
              <Select
                {...field}
                error={!!errorMessage}
                helperText={errorMessage}
                value={field.value?.toString().toLowerCase() ?? ''}
                onChange={(e) => field.onChange(e.target.value.toLowerCase())}
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          )}
        />
      );
    } else {
      return (
        <Controller
          name={item.parametername}
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: field.value.length > 0 && (
                  <InputAdornment position="start">
                    <IconButton
                      size="small"
                      onClick={() =>
                        field.onChange("")
                      }
                      edge="start"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              className={classes.textField}
              error={!!errorMessage}
              helperText={errorMessage}
            />
          )}
        />
      );
    }
  };


  return (
    <>
      <Grid item xs={12} sm={12} className={clsx(classes.drawerItems)}>
        <Typography variant="h5">Report Parameters</Typography>
        <Divider />
      </Grid>
      <Grid item xs={12} sm={12} className={clsx(classes.drawerItems)}>
        {items.length === 0 ? (
          <Typography>Please select report name.</Typography>
        ) : (
          <div className={classes.scrollableContainer}>
            <List>
              {Object.entries(groupedItems).map(([group, groupItems]) => {
                const isOpen = group === "" ? true : openGroups[group];

                return (
                  <React.Fragment key={group || "ungrouped"}>
                    {group !== "" && (
                      <ListItem
                        button
                        onClick={() => toggleGroup(group)}
                        className={classes.listItem}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" color="primary">
                              {group}
                            </Typography>
                          }
                        />
                        {isOpen ? <IconExpandLess /> : <IconExpandMore />}
                      </ListItem>
                    )}
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      {groupItems.map((item) => {
                        const errorMessage = _.get(errors, item.parametername + ".message", "");

                        if (item.parametername === "selectedFacilities") {
                          if (!information.facilityGroupID || !item.visible) {
                            return null;
                          }
                        }

                        if (item.parametername === "separatebyfacility" && !information.facilityGroupID) {
                          return null;
                        }

                        return (
                          <ListItem key={item.parameterid} className={classes.listItem}>
                            <Typography variant="body2" className={classes.typeLabel}>
                              {item.description.includes(".")
                                ? item.description.split(".")[1]
                                : item.description
                              }
                            </Typography>
                            {renderInput(item, errorMessage)}
                          </ListItem>
                        );
                      })}
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
          </div>
        )}
      </Grid>
    </>
  );
}
export default ReportParametersList;